/**
 * Cart Popup Notification System
 * Handles popup when products are added to cart and special cart count logic
 */

class CartPopup {
  constructor() {
    this.popup = null;
    this.autoHideTimeout = null;
    this.autoHideDelay = 8000;
    this.cartIconClickHandler = this.handleCartIconClick.bind(this);
    this.productInfo = this.loadProductInfo();
    this.meterText = '';
    
    this.init();
  }

  init() {
    this.popup = document.querySelector('[data-cart-popup]');
    if (!this.popup) return;
    
    this.meterText = this.popup.dataset.meterText || 'Meter';
    
    // Add event listener for cart icon click
    const cartIcon = document.querySelector('[data-header-cart-link]');
    if (cartIcon) {
      cartIcon.addEventListener('click', this.cartIconClickHandler.bind(this));
    }
    
    // Add event listener for close button
    const closeButton = this.popup.querySelector('[data-cart-popup-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }
    
    // Add event listener for overlay click
    const overlay = this.popup.querySelector('[data-cart-popup-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => this.hide());
    }
    
    // Add event listener for continue shopping button
    const continueButton = this.popup.querySelector('[data-cart-popup-continue]');
    if (continueButton) {
      continueButton.addEventListener('click', () => this.hide());
    }
    
    // Add event listener for cart item added
    document.addEventListener('cartItemAdded', async (event) => {
      // Get fresh cart data and product info
      const [cart, freshProductInfo] = await Promise.all([
        fetch('/cart.js').then(res => res.json()),
        window.getProductInfo()
      ]);
      
      // Update our product info
      this.productInfo = freshProductInfo;
      
      // Show popup with notification
      this.showWithNotification(event.detail, cart);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isVisible()) {
        this.hide();
      }
    });

    window.addEventListener('resize', () => {
      if (this.isVisible()) {
        this.positionPopup();
      }
    });
  }

  loadProductInfo() {
    const productInfoScript = document.querySelector('[data-product-info]');
    if (productInfoScript) {
      try {
        return JSON.parse(productInfoScript.textContent);
      } catch (e) {
        console.error('Error parsing product info:', e);
        return {};
      }
    }
    return {};
  }

  calculateMeters(quantity) {
    // Each quantity unit is 10cm, so divide by 10 to get meters
    return (quantity * 0.1).toFixed(1);
  }

  async handleCartIconClick(event) {
    event.preventDefault();
    await this.showAllCartItems();
  }

  async showAllCartItems() {
    try {
      // Get fresh cart data and product info
      const [cart, freshProductInfo] = await Promise.all([
        fetch('/cart.js').then(res => res.json()),
        window.getProductInfo()
      ]);
      
      // Update our product info
      this.productInfo = freshProductInfo;
      
      await this.updatePopupContent(null, cart);
      this.show(false);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  show(shouldAutoHide = false) {
    if (!this.popup) {
      console.warn('Cart popup element not found - make sure cart-popup.liquid is included');
      return;
    }

    this.positionPopup();

    this.popup.style.display = 'block';
    
    requestAnimationFrame(() => {
      this.popup.classList.add('cart-popup--visible');
    });

    if (shouldAutoHide) {
      this.startAutoHide();
    } else if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }
  }

  positionPopup() {
    const cartIcon = document.querySelector('[data-header-cart-link]');
    const container = this.popup.querySelector('.cart-popup__container');
    
    if (!cartIcon || !container) return;

    const cartRect = cartIcon.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    const containerWidth = Math.min(380, viewportWidth - 32); 
    
    let top = cartRect.bottom + 12; 
    let left = cartRect.right - containerWidth + 10; 
    
    if (left < 16) {
      left = 16;
    } else if (left + containerWidth > viewportWidth - 16) {
      left = viewportWidth - containerWidth - 16; 
    }
    
    container.style.top = `${Math.max(16, top)}px`;
    container.style.left = `${left}px`;

    const cartCenter = cartRect.left + (cartRect.width / 2);
    const containerLeft = left;
    const arrowOffset = Math.max(20, Math.min(containerWidth - 36, cartCenter - containerLeft));
    container.style.setProperty('--arrow-left', `${arrowOffset}px`);
  }

  hide() {
    if (!this.popup) return;

    this.popup.classList.remove('cart-popup--visible');
    
    setTimeout(() => {
      this.popup.style.display = 'none';
    }, 300);

    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }
  }

  isVisible() {
    return this.popup && this.popup.classList.contains('cart-popup--visible');
  }

  startAutoHide() {
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
    }

    this.autoHideTimeout = setTimeout(() => {
      this.hide();
    }, this.autoHideDelay);
  }

  async showWithNotification(productData, cart) {
    await this.updatePopupContent(productData, cart);
    this.show(true); // Auto-hide when showing notification
  }

  async updatePopupContent(productData, cart = null) {
    if (!cart) {
      try {
        // Get fresh cart data and product info
        const [freshCart, freshProductInfo] = await Promise.all([
          fetch('/cart.js').then(res => res.json()),
          window.getProductInfo()
        ]);
        
        cart = freshCart;
        this.productInfo = freshProductInfo;
      } catch (error) {
        console.error('Error fetching cart:', error);
        return;
      }
    }

    const itemsContainer = this.popup.querySelector('[data-cart-popup-items]');
    const subtotalElement = this.popup.querySelector('[data-cart-popup-subtotal]');
    
    if (!itemsContainer) return;

    let itemsHtml = '';
    let subtotal = 0;

    for (const item of cart.items) {
      // Get product type info from embedded JSON or window object
      const productInfo = this.productInfo[item.product_id] || window.specialProducts[item.product_id];
      const isFabricOrMinQty = productInfo?.hasFabricTag || productInfo?.hasMinimumQuantity;
      
      // For display purposes
      const displayQuantity = isFabricOrMinQty ? 1 : item.quantity;
      
      // Calculate meters for fabric products
      const meters = productInfo?.hasMinimumQuantity ? this.calculateMeters(item.quantity) : null;
      
      // Use the total line price for the item
      const itemPrice = item.final_line_price;  // Already in cents
      subtotal += itemPrice;

      const isHighlighted = productData && item.key === productData.key;
      
      itemsHtml += `
        <div class="cart-popup__item${isHighlighted ? ' cart-popup__item--highlighted' : ''}" data-item-key="${item.key}">
          <img src="${item.image}" alt="${item.title}" class="cart-popup__item-image">
          <div class="cart-popup__item-details">
            <a href="${item.url}">
              <h4 class="cart-popup__item-title">${item.title}</h4>
            </a>
            ${item.variant_title ? `<p class="cart-popup__item-variant">${item.variant_title}</p>` : ''}
            <div class="cart-popup__item-price">
              <span class="cart-popup__item-quantity">${displayQuantity} x</span>
              <span>${Shopify.formatMoney(itemPrice, window.Theme.moneyFormat)}</span>
            </div>
            ${meters ? `<div class="cart-popup__item-meters"><span class="cart-popup__item-meters-label">${this.meterText}:</span> ${meters}</div>` : ''}
          </div>
          <button type="button" class="cart-popup__item-remove" data-remove-item aria-label="{{ 'cart.popup.remove_item' | t }}">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;
    }

    itemsContainer.innerHTML = itemsHtml || '<p class="cart-popup__empty">Your cart is empty</p>';
    
    if (subtotalElement) {
      subtotalElement.textContent = Shopify.formatMoney(subtotal, window.Theme.moneyFormat);
    }

    // Add event listeners for remove buttons
    const removeButtons = itemsContainer.querySelectorAll('[data-remove-item]');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleRemoveItem(e));
    });
  }

  async handleRemoveItem(event) {
    const item = event.target.closest('[data-item-key]');
    if (!item) return;

    const key = item.dataset.itemKey;
    
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      // Get fresh cart data and product info
      const [cart, freshProductInfo] = await Promise.all([
        fetch('/cart.js').then(res => res.json()),
        window.getProductInfo()
      ]);
      
      // Update our product info
      this.productInfo = freshProductInfo;
      
      // Update cart count
      if (window.updateCartCountDirect) {
        window.updateCartCountDirect();
      }
      
      // Update popup content
      await this.updatePopupContent(null, cart);
      
      // Hide popup if cart is empty
      if (cart.item_count === 0) {
        this.hide();
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  async updateCartCount() {
    try {
      const [cartResponse, productInfoResponse] = await Promise.all([
        fetch('/cart.js').then(res => res.json()),
        fetch('/?sections=cart-popup').then(res => res.json())
      ]);
      
      const cart = cartResponse;
      
      // Get fresh product info
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = productInfoResponse['cart-popup'];
      const productInfoScript = tempDiv.querySelector('[data-product-info]');
      const productInfo = productInfoScript ? JSON.parse(productInfoScript.textContent) : {};
      
      let displayCount = 0;
      
      for (const item of cart.items) {
        const itemInfo = productInfo[item.variant_id] || productInfo[item.product_id];
        const isFabricOrMinQty = itemInfo?.hasFabricTag || itemInfo?.hasMinimumQuantity;
        
        if (isFabricOrMinQty) {
          displayCount += 1;
        } else {
          displayCount += item.quantity;
        }
      }
      
      return displayCount;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return document.querySelector('[data-header-cart-count]')?.textContent?.replace(/[()]/g, '') || '0';
    }
  }
}

class CartCountManager {
  constructor() {
    this.cartPopup = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    setTimeout(() => {
      this.cartPopup = window.cartPopupInstance;
    }, 100);
  }

  async updateHeaderCartCount() {
    const headerCartCount = document.querySelector('[data-header-cart-count]');
    if (!headerCartCount || !this.cartPopup) return;

    try {
      const count = await this.cartPopup.updateCartCount();
      
      const currentText = headerCartCount.textContent.trim();
      const currentCount = currentText ? parseInt(currentText.replace(/[()]/g, '')) : 0;
      
      if (currentCount !== count) {
        if (count > 0) {
          headerCartCount.textContent = `(${count})`;
          headerCartCount.style.display = '';
        } else {
          headerCartCount.textContent = '';
          headerCartCount.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Error updating header cart count:', error);
    }
  }
}

let cartPopupInstance = null;
let cartCountManager = null;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartComponents);
} else {
  initializeCartComponents();
}

function initializeCartComponents() {
  cartPopupInstance = new CartPopup();
  cartCountManager = new CartCountManager();
  
  window.cartPopupInstance = cartPopupInstance;
  window.cartCountManager = cartCountManager;
}

window.updateCartWithSpecialCount = async function() {
  if (window.cartCountManager) {
    if (window.cartCountManager.cartPopup) {
      await window.cartCountManager.updateHeaderCartCount();
    } else {
      window.cartCountManager.cartPopup = window.cartPopupInstance;
      if (window.cartCountManager.cartPopup) {
        await window.cartCountManager.updateHeaderCartCount();
      }
    }
  } else if (window.cartPopupInstance) {
    const count = await window.cartPopupInstance.updateCartCount();
    const headerCartCount = document.querySelector('[data-header-cart-count]');
    
    if (headerCartCount) {
      const currentText = headerCartCount.textContent.trim();
      const currentCount = currentText ? parseInt(currentText.replace(/[()]/g, '')) : 0;
      
      if (currentCount !== count) {
        if (count > 0) {
          headerCartCount.textContent = `(${count})`;
          headerCartCount.style.display = '';
        } else {
          headerCartCount.textContent = '';
          headerCartCount.style.display = 'none';
        }
      }
    }
  }
}; 